import { useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import AssistantButton from "@/components/AssistantButton";
import { confirmLeave, useUnsavedTracking } from "@/lib/unsavedChanges";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Building2, FolderOpenDot, ShieldCheck, BookMarked, Settings,
  ScrollText, Users, LogOut, ChevronDown, FileSpreadsheet, Landmark, AlertTriangle, GraduationCap, Activity, DatabaseBackup, Menu,
} from "lucide-react";

const NAV = [
  { section: null, items: [
    { to: "/", label: "Beranda", icon: LayoutDashboard, testid: "nav-home" },
    { to: "/clients", label: "Klien", icon: Building2, testid: "nav-clients" },
    { to: "/case-study", label: "Studi Kasus Audit", icon: GraduationCap, testid: "nav-case-study" },
  ]},
  { section: "PERIKATAN", items: [
    { to: "/m/acceptance", label: "Penerimaan & Keberlanjutan", icon: ShieldCheck, testid: "nav-acceptance" },
    { to: "/m/account-workspace", label: "Kertas Kerja Akun", icon: FileSpreadsheet, testid: "nav-account-workspace" },
    { to: "/m/data-intake", label: "Data Intake", icon: FileSpreadsheet, testid: "nav-dataintake" },
    { to: "/m/fraud-jet", label: "Fraud & JET Center", icon: AlertTriangle, testid: "nav-fraud-jet" },
    { to: "/m/sampling", label: "Sampling Center", icon: FolderOpenDot, testid: "nav-sampling" },
    { to: "/m/confirmations", label: "Konfirmasi", icon: Landmark, testid: "nav-confirmations" },
  ]},
  { section: "MANAJEMEN KUALITAS", items: [
    { to: "/m/smm", label: "SMM Dashboard", icon: ShieldCheck, testid: "nav-smm" },
    { to: "/m/eqr", label: "EQR", icon: ShieldCheck, testid: "nav-eqr" },
  ]},
  { section: "METODOLOGI", items: [
    { to: "/methodology", label: "Standar & Metodologi", icon: BookMarked, testid: "nav-methodology" },
    { to: "/program-library", label: "Pustaka Program", icon: BookMarked, testid: "nav-program-library" },
    { to: "/standards-library", label: "Pustaka Standar", icon: BookMarked, testid: "nav-standards-library" },
    { to: "/risk-library", label: "Pustaka Risiko", icon: BookMarked, testid: "nav-risk-library" },
  ]},
  { section: "ARSIP & ADMIN", items: [
    { to: "/admin/users", label: "Pengguna & Peran", icon: Users, testid: "nav-users", adminOnly: true },
    { to: "/admin/audit-logs", label: "Log Audit", icon: ScrollText, testid: "nav-logs" },
    { to: "/admin/integration-health", label: "Integration Health", icon: Activity, testid: "nav-integration-health", adminOnly: true },
    { to: "/admin/backup-recovery", label: "Backup & Pemulihan", icon: DatabaseBackup, testid: "nav-backup-recovery", superAdminOnly: true },
    { to: "/firm/settings", label: "Pengaturan KAP", icon: Settings, testid: "nav-firm-settings" },
  ]},
];

// `assistant` is the page's context for the global AI help button: { engagementId, engagementName, panel,
// panelLabel, reload } on an engagement, { pageLabel } elsewhere. `assistantOpen` increments to open it.
export default function Layout({ children, assistant, assistantOpen = 0 }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const content = useRef(null);
  useUnsavedTracking(content);
  // Every way out of a page with unsaved edits asks first (lib/unsavedChanges.js).
  const guard = (then) => (event) => { if (!confirmLeave()) { event.preventDefault(); return; } then?.(); };
  // Phones get the navigation as a drawer: a fixed 256px sidebar on a 390px screen leaves the page a
  // strip and pushes it sideways. From 768px up the sidebar is always shown, exactly as before.
  const [drawer, setDrawer] = useState(false);

  const sidebar = (onNavigate) => (
    <>
        <div className="px-4 py-4 border-b border-white/10">
          <div className="font-head text-lg font-bold text-white tracking-tight">AUDIT OS</div>
          <div className="text-[10px] uppercase tracking-widest text-sky-300/80">Indonesia · KAP</div>
          <div className="mt-2 inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="uat-banner">Foundation · Enhancement in progress</div>
        </div>
        <nav className="flex-1 overflow-y-auto scroll-thin py-3">
          {NAV.map((grp, gi) => (
            <div key={gi} className="mb-4">
              {grp.section && (
                <div className="px-4 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">{grp.section}</div>
              )}
              {grp.items.filter((it) => (!it.adminOnly || ["Super Admin", "Firm Admin"].includes(user?.role)) && (!it.superAdminOnly || user?.role === "Super Admin")).map((it) => (
                <NavLink key={it.to} to={it.to} data-testid={it.testid} onClick={guard(onNavigate)}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-2.5 mx-2 px-3 py-2 rounded-md text-[13px] transition-colors ${
                      isActive
                        ? "bg-white/10 text-white font-medium"
                        : "text-slate-300 hover:bg-white/5 hover:text-white"
                    }`}>
                  {({ isActive }) => (
                    <>
                      {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-sky-400" />}
                      <it.icon size={16} strokeWidth={1.9} className={isActive ? "text-sky-300" : "text-slate-400 group-hover:text-slate-200"} />
                      <span>{it.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="text-xs text-white font-medium truncate">{user?.name}</div>
          <div className="text-[11px] text-sky-300/80 mb-2">{user?.role}</div>
          <button onClick={(e) => guard(() => { logout(); navigate("/login"); })(e)} data-testid="logout-btn"
            className="flex items-center gap-2 text-[12px] text-slate-300 hover:text-white transition-colors">
            <LogOut size={14} /> Keluar
          </button>
        </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden md:flex w-64 shrink-0 bg-[#0B2038] text-slate-300 flex-col fixed h-screen z-20 shadow-card-lg" data-testid="sidebar">
        {sidebar()}
      </aside>
      {drawer && (
        <div className="md:hidden fixed inset-0 z-40 flex" data-testid="mobile-drawer">
          <aside className="w-64 max-w-[85vw] bg-[#0B2038] text-slate-300 flex flex-col h-full shadow-card-lg">{sidebar(() => setDrawer(false))}</aside>
          <button type="button" aria-label="Tutup menu" className="flex-1 bg-black/40" onClick={() => setDrawer(false)} data-testid="mobile-drawer-close" />
        </div>
      )}
      <main className="flex-1 md:ml-64 min-w-0">
        <div className="md:hidden flex items-center gap-3 bg-[#0B2038] text-white px-4 h-11" data-testid="mobile-topbar">
          <button type="button" aria-label="Buka menu" onClick={() => setDrawer(true)} data-testid="mobile-menu"><Menu size={20} /></button>
          <span className="font-head font-bold tracking-tight">AUDIT OS</span>
        </div>
        <div ref={content}>{children}</div>
      </main>
      <AssistantButton context={assistant} role={user?.role} openRequest={assistantOpen} />
    </div>
  );
}

export function PageHeader({ title, subtitle, breadcrumb, actions }) {
  return (
    <div className="sticky top-0 z-10 bg-white border-b border-border px-4 sm:px-6 py-3.5 flex flex-wrap items-center justify-between gap-2 shadow-card">
      <div className="min-w-0">
        {breadcrumb && <div className="text-[11px] font-medium uppercase tracking-wider text-navy-500/70 mb-0.5">{breadcrumb}</div>}
        <h1 className="font-head text-lg sm:text-[22px] leading-tight font-bold text-navy-700 truncate">{title}</h1>
        {subtitle && <div className="text-xs text-muted-foreground mt-0.5 break-words">{subtitle}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2 min-w-0">{actions}</div>
    </div>
  );
}
